import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { TCN } from '../ui-kit/tokens';
import Icon from '../ui-kit/Icon';
import Avatar from '../ui-kit/Avatar';
import LanguageMenu from '../ui-kit/LanguageMenu';
import useUnreadAlerts from '../../tcn-modules/m7/useUnreadAlerts';
import { TCN_TABS } from './TabBar';

/**
 * TCN Desktop shell — fixed left sidebar (80px collapsed / 220px expanded)
 * with logo + 5 nav items + bell + avatar at the bottom.
 * Top bar with breadcrumb + search. Main content scrollable.
 *
 * Props:
 *  - tab: active tab id (one of TCN_TABS ids)
 *  - onTabChange: (id) => void
 *  - breadcrumb: string | string[]
 *  - userName: string for the bottom avatar
 *  - onBell: callback
 *  - rightSlot: optional custom trailing slot in the top bar
 *  - sidebarExpanded: boolean (default false)
 *  - children: main content
 */
const TcnDesktopShell = ({
  tab,
  onTabChange,
  breadcrumb,
  userName = 'Agent',
  onBell,
  rightSlot,
  sidebarExpanded: expandedProp,
  children,
  className = '',
  style,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count: unreadCount } = useUnreadAlerts(user?.id);
  const [hovered, setHovered] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const expanded = expandedProp ?? hovered;
  const sidebarWidth = expanded ? 220 : 80;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  const crumbs = Array.isArray(breadcrumb) ? breadcrumb : breadcrumb ? [breadcrumb] : [];

  return (
    <div
      className={`flex h-[100dvh] w-screen ${className}`}
      style={{
        background: TCN.bg,
        color: TCN.text,
        fontFamily: TCN.fontSans,
        ...style,
      }}
    >
      {/* === Sidebar === */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: sidebarWidth,
          background: TCN.surface1,
          borderInlineEnd: `1px solid ${TCN.border}`,
          display: 'flex',
          flexDirection: 'column',
          transition: `width ${TCN.durBase} ${TCN.ease}`,
          flexShrink: 0,
        }}
      >
        {/* Logo */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            paddingInlineStart: 24,
            paddingInlineEnd: 16,
            borderBottom: `1px solid ${TCN.border}`,
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${TCN.cyan}, ${TCN.diamond})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '-0.05em',
              flexShrink: 0,
            }}
          >
            T
          </div>
          {expanded && (
            <span
              style={{
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap',
              }}
            >
              TCN Agent
            </span>
          )}
        </div>

        {/* Nav */}
        <nav
          style={{
            flex: 1,
            padding: '14px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {TCN_TABS.map((entry) => {
            const isActive = entry.id === tab;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => onTabChange?.(entry.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: isActive ? `${TCN.cyan}1f` : 'transparent',
                  color: isActive ? TCN.cyan : TCN.textDim,
                  border: isActive ? `1px solid ${TCN.cyan}33` : '1px solid transparent',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  textAlign: 'start',
                  transition: `all ${TCN.durBase} ${TCN.ease}`,
                  fontFamily: TCN.fontSans,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Icon name={entry.icon} size={20} strokeWidth={isActive ? 2 : 1.6} />
                {expanded && <span>{t(entry.i18n, entry.fallback)}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom: bell + avatar */}
        <div
          style={{
            borderTop: `1px solid ${TCN.border}`,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onBell ?? (() => navigate('/tcn/me/alerts'))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              background: 'transparent',
              color: unreadCount > 0 ? TCN.cyan : TCN.textDim,
              border: '1px solid transparent',
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: TCN.fontSans,
              position: 'relative',
            }}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon name="bell" size={20} />
              {unreadCount > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -4,
                    insetInlineEnd: -6,
                    minWidth: unreadCount > 9 ? 18 : 14,
                    height: 14,
                    padding: unreadCount > 9 ? '0 4px' : 0,
                    background: TCN.red,
                    color: '#fff',
                    borderRadius: 7,
                    fontSize: 9,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 0 6px ${TCN.red}66`,
                    lineHeight: 1,
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </span>
            {expanded && (
              <span>
                {t('tcn.nav.alerts', 'Alerts')}
                {unreadCount > 0 && (
                  <span style={{ color: TCN.textMute, fontWeight: 500, marginInlineStart: 6 }}>
                    {unreadCount}
                  </span>
                )}
              </span>
            )}
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '6px 8px',
            }}
          >
            <Avatar name={userName} size={32} ring />
            {expanded && (
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: TCN.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {userName}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: TCN.textMute,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('tcn.role.agent', 'Agent')}
                </div>
              </div>
            )}
          </div>
          {/* Language switcher — gated by tenant.enabled_languages */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setLangOpen((o) => !o)}
              title={t('tcn.account.language', 'Language')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 12px',
                width: '100%',
                background: langOpen ? `${TCN.cyan}10` : 'transparent',
                color: langOpen ? TCN.cyan : TCN.textDim,
                border: `1px solid ${langOpen ? `${TCN.cyan}33` : 'transparent'}`,
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: TCN.fontSans,
              }}
            >
              <Icon name="translate" size={20} />
              {expanded && <span style={{ flex: 1, textAlign: 'start' }}>{t('tcn.account.language', 'Language')}</span>}
            </button>
            {langOpen && (
              <>
                <div
                  onClick={() => setLangOpen(false)}
                  role="presentation"
                  style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'transparent' }}
                />
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 6px)',
                    insetInlineStart: 0,
                    minWidth: 200,
                    padding: 10,
                    background: TCN.surface1,
                    border: `1px solid ${TCN.border}`,
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
                    zIndex: 100,
                  }}
                >
                  <LanguageMenu compact onAfter={() => setLangOpen(false)} />
                </div>
              </>
            )}
          </div>
          {/* Sign out — always visible at bottom */}
          <button
            type="button"
            onClick={handleSignOut}
            title={t('tcn.nav.signOut', 'Sign out')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              background: 'transparent',
              color: TCN.textDim,
              border: `1px solid transparent`,
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: TCN.fontSans,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = TCN.red; e.currentTarget.style.background = `${TCN.red}10`; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = TCN.textDim; e.currentTarget.style.background = 'transparent'; }}
          >
            <Icon name="arrow-right" size={20} />
            {expanded && <span>{t('tcn.nav.signOut', 'Sign out')}</span>}
          </button>
        </div>
      </aside>

      {/* === Main column === */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <header
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            borderBottom: `1px solid ${TCN.border}`,
            background: TCN.bg,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {crumbs.length > 0 && crumbs.map((c, i) => (
              <React.Fragment key={i}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: i === crumbs.length - 1 ? TCN.text : TCN.textDim,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c}
                </span>
                {i < crumbs.length - 1 && (
                  <Icon name="chevron" size={14} color={TCN.textMute} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {rightSlot}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: TCN.surface1,
                border: `1px solid ${TCN.border}`,
                borderRadius: 10,
                color: TCN.textDim,
                fontSize: 13,
                width: 220,
              }}
            >
              <Icon name="search" size={16} />
              <span>{t('tcn.search.placeholder', 'Search creators…')}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main
          style={{
            flex: 1,
            overflow: 'auto',
            background: TCN.bg,
            padding: 24,
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default TcnDesktopShell;
